// Grouped client for Ai operations
import { ApiConfiguration } from '../ApiConfiguration';
import { Form } from '../models/Form';
import { DataFormActionRequest } from '../models/DataFormActionRequest';
import { DataFormActionResponse } from '../models/DataFormActionResponse';
import { AiAssistant } from '../models/AiAssistant';
import { FilterAiAssistantRequest } from '../models/FilterAiAssistantRequest';
import { DataviewAiAssistantResponse } from '../models/DataviewAiAssistantResponse';
import { StartAiChatUploadImageRequest } from '../models/StartAiChatUploadImageRequest';
import { AiChat } from '../models/AiChat';
import { FilterAiChatRequest } from '../models/FilterAiChatRequest';
import { DataviewAiChatResponse } from '../models/DataviewAiChatResponse';
import { ContentResult } from '../models/ContentResult';
import { FilterContentResultRequest } from '../models/FilterContentResultRequest';
import { DataviewContentResultResponse } from '../models/DataviewContentResultResponse';
import { Prompt } from '../models/Prompt';
import { UpdatePromptRequest } from '../models/UpdatePromptRequest';
import { CreatePromptRequest } from '../models/CreatePromptRequest';
import { PromptFields } from '../models/PromptFields';
import { FilterPromptRequest } from '../models/FilterPromptRequest';
import { DataviewPromptResponse } from '../models/DataviewPromptResponse';
import { FormSerializer } from '../serializers/FormSerializer';
import { DataFormActionRequestSerializer } from '../serializers/DataFormActionRequestSerializer';
import { DataFormActionResponseSerializer } from '../serializers/DataFormActionResponseSerializer';
import { AiAssistantSerializer } from '../serializers/AiAssistantSerializer';
import { FilterAiAssistantRequestSerializer } from '../serializers/FilterAiAssistantRequestSerializer';
import { DataviewAiAssistantResponseSerializer } from '../serializers/DataviewAiAssistantResponseSerializer';
import { StartAiChatUploadImageRequestSerializer } from '../serializers/StartAiChatUploadImageRequestSerializer';
import { AiChatSerializer } from '../serializers/AiChatSerializer';
import { FilterAiChatRequestSerializer } from '../serializers/FilterAiChatRequestSerializer';
import { DataviewAiChatResponseSerializer } from '../serializers/DataviewAiChatResponseSerializer';
import { ContentResultSerializer } from '../serializers/ContentResultSerializer';
import { FilterContentResultRequestSerializer } from '../serializers/FilterContentResultRequestSerializer';
import { DataviewContentResultResponseSerializer } from '../serializers/DataviewContentResultResponseSerializer';
import { PromptSerializer } from '../serializers/PromptSerializer';
import { UpdatePromptRequestSerializer } from '../serializers/UpdatePromptRequestSerializer';
import { CreatePromptRequestSerializer } from '../serializers/CreatePromptRequestSerializer';
import { FilterPromptRequestSerializer } from '../serializers/FilterPromptRequestSerializer';
import { DataviewPromptResponseSerializer } from '../serializers/DataviewPromptResponseSerializer';

/**
 * HTTP client for Ai operations
 *
 * Client for Ai operations
 */
export class AiClient {
  private readonly config: ApiConfiguration;

  constructor(config: ApiConfiguration) {
    this.config = config;
  }

  async getFormForaiAssistant(formName: string, objectId: string, tags?: string[] | null, isActive?: boolean | null, profileIds?: string[] | null, role?: string | null, createdOn?: string | null, id?: string | null, name?: string | null, description?: string | null, entityId?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.Assistant({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (tags !== undefined) {
      queryParams.append('Tags', String(tags));
    }
    if (isActive !== undefined) {
      queryParams.append('IsActive', String(isActive));
    }
    if (profileIds !== undefined) {
      queryParams.append('ProfileIds', String(profileIds));
    }
    if (role !== undefined) {
      queryParams.append('Role', String(role));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeaiAssistant(formName: string, tags?: string[] | null, isActive?: boolean | null, profileIds?: string[] | null, role?: string | null, createdOn?: string | null, id?: string | null, name?: string | null, description?: string | null, entityId?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.Assistant/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (tags !== undefined) {
      queryParams.append('Tags', String(tags));
    }
    if (isActive !== undefined) {
      queryParams.append('IsActive', String(isActive));
    }
    if (profileIds !== undefined) {
      queryParams.append('ProfileIds', String(profileIds));
    }
    if (role !== undefined) {
      queryParams.append('Role', String(role));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterAiAssistant(request?: FilterAiAssistantRequest | null, options?: { headers?: Record<string, string> }): Promise<AiAssistant[]> {
    const path = '/app/api/Object/ai.Assistant/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterAiAssistantRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => AiAssistantSerializer.fromObject(item));
  }

  async recentAiAssistant(request?: FilterAiAssistantRequest | null, options?: { headers?: Record<string, string> }): Promise<AiAssistant[]> {
    const path = '/app/api/Object/ai.Assistant/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterAiAssistantRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => AiAssistantSerializer.fromObject(item));
  }

  async dataViewAiAssistant(request?: FilterAiAssistantRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewAiAssistantResponse> {
    const path = '/app/api/Object/ai.Assistant/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterAiAssistantRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewAiAssistantResponseSerializer.fromObject(data);
  }

  async runAiChatOpenHtmlAction(objectId: string, request?: DataFormActionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(7651c6be-6e01-4c20-b7e1-d59d17aeb868)/DataForm'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(DataFormActionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async startAiChatOpenHtml(objectId: string, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(7651c6be-6e01-4c20-b7e1-d59d17aeb868)'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async runAiChatCopyAction(objectId: string, request?: DataFormActionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(71052944-8be3-4cb9-bef0-c4386f2e4685)/DataForm'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(DataFormActionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async startAiChatCopy(objectId: string, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(71052944-8be3-4cb9-bef0-c4386f2e4685)'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async runAiChatShareAction(objectId: string, request?: DataFormActionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(f471faeb-f256-4fe7-a57e-2c26c460876d)/DataForm'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(DataFormActionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async startAiChatShare(objectId: string, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(f471faeb-f256-4fe7-a57e-2c26c460876d)'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async getAiChatUploadImageForm(objectId: string, fileName?: string | null, contentType?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(824be7a0-fb61-4838-96a4-1bc62c27e8f0)/DataForm'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (fileName !== undefined) {
      queryParams.append('fileName', String(fileName));
    }
    if (contentType !== undefined) {
      queryParams.append('contentType', String(contentType));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async runAiChatUploadImageAction(objectId: string, request?: DataFormActionRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(824be7a0-fb61-4838-96a4-1bc62c27e8f0)/DataForm'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(DataFormActionRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async startAiChatUploadImage(objectId: string, request?: StartAiChatUploadImageRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Chat({objectId})/UserAction(824be7a0-fb61-4838-96a4-1bc62c27e8f0)'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(StartAiChatUploadImageRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async getFormForaiChat(formName: string, objectId: string, flowId?: string | null, objectStatusId?: string | null, lastMessage?: any | null, name?: string | null, description?: string | null, entityId?: string | null, id?: string | null, createdOn?: string | null, embeddedContentContent?: string | null, embeddedContentContentType?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.Chat({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (flowId !== undefined) {
      queryParams.append('FlowId', String(flowId));
    }
    if (objectStatusId !== undefined) {
      queryParams.append('ObjectStatusId', String(objectStatusId));
    }
    if (lastMessage !== undefined) {
      queryParams.append('LastMessage', String(lastMessage));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (embeddedContentContent !== undefined) {
      queryParams.append('EmbeddedContent|Content', String(embeddedContentContent));
    }
    if (embeddedContentContentType !== undefined) {
      queryParams.append('EmbeddedContent|ContentType', String(embeddedContentContentType));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeaiChat(formName: string, flowId?: string | null, objectStatusId?: string | null, lastMessage?: any | null, name?: string | null, description?: string | null, entityId?: string | null, id?: string | null, createdOn?: string | null, embeddedContentContent?: string | null, embeddedContentContentType?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.Chat/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (flowId !== undefined) {
      queryParams.append('FlowId', String(flowId));
    }
    if (objectStatusId !== undefined) {
      queryParams.append('ObjectStatusId', String(objectStatusId));
    }
    if (lastMessage !== undefined) {
      queryParams.append('LastMessage', String(lastMessage));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (embeddedContentContent !== undefined) {
      queryParams.append('EmbeddedContent|Content', String(embeddedContentContent));
    }
    if (embeddedContentContentType !== undefined) {
      queryParams.append('EmbeddedContent|ContentType', String(embeddedContentContentType));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterAiChat(request?: FilterAiChatRequest | null, options?: { headers?: Record<string, string> }): Promise<AiChat[]> {
    const path = '/app/api/Object/ai.Chat/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterAiChatRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => AiChatSerializer.fromObject(item));
  }

  async recentAiChat(request?: FilterAiChatRequest | null, options?: { headers?: Record<string, string> }): Promise<AiChat[]> {
    const path = '/app/api/Object/ai.Chat/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterAiChatRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => AiChatSerializer.fromObject(item));
  }

  async dataViewAiChat(request?: FilterAiChatRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewAiChatResponse> {
    const path = '/app/api/Object/ai.Chat/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterAiChatRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewAiChatResponseSerializer.fromObject(data);
  }

  async getFormForContentResult(formName: string, objectId: string, errorMessage?: string | null, flowRunId?: string | null, resultType?: string | null, success?: boolean | null, name?: string | null, description?: string | null, entityId?: string | null, id?: string | null, createdOn?: string | null, resultAssistantFeedback?: string | null, resultChatDescription?: string | null, resultChatName?: string | null, resultContent?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.ContentFlowRunResult({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (errorMessage !== undefined) {
      queryParams.append('ErrorMessage', String(errorMessage));
    }
    if (flowRunId !== undefined) {
      queryParams.append('FlowRunId', String(flowRunId));
    }
    if (resultType !== undefined) {
      queryParams.append('ResultType', String(resultType));
    }
    if (success !== undefined) {
      queryParams.append('Success', String(success));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (resultAssistantFeedback !== undefined) {
      queryParams.append('Result|AssistantFeedback', String(resultAssistantFeedback));
    }
    if (resultChatDescription !== undefined) {
      queryParams.append('Result|ChatDescription', String(resultChatDescription));
    }
    if (resultChatName !== undefined) {
      queryParams.append('Result|ChatName', String(resultChatName));
    }
    if (resultContent !== undefined) {
      queryParams.append('Result|Content', String(resultContent));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeContentResult(formName: string, errorMessage?: string | null, flowRunId?: string | null, resultType?: string | null, success?: boolean | null, name?: string | null, description?: string | null, entityId?: string | null, id?: string | null, createdOn?: string | null, resultAssistantFeedback?: string | null, resultChatDescription?: string | null, resultChatName?: string | null, resultContent?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.ContentFlowRunResult/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (errorMessage !== undefined) {
      queryParams.append('ErrorMessage', String(errorMessage));
    }
    if (flowRunId !== undefined) {
      queryParams.append('FlowRunId', String(flowRunId));
    }
    if (resultType !== undefined) {
      queryParams.append('ResultType', String(resultType));
    }
    if (success !== undefined) {
      queryParams.append('Success', String(success));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    if (resultAssistantFeedback !== undefined) {
      queryParams.append('Result|AssistantFeedback', String(resultAssistantFeedback));
    }
    if (resultChatDescription !== undefined) {
      queryParams.append('Result|ChatDescription', String(resultChatDescription));
    }
    if (resultChatName !== undefined) {
      queryParams.append('Result|ChatName', String(resultChatName));
    }
    if (resultContent !== undefined) {
      queryParams.append('Result|Content', String(resultContent));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterContentResult(request?: FilterContentResultRequest | null, options?: { headers?: Record<string, string> }): Promise<ContentResult[]> {
    const path = '/app/api/Object/ai.ContentFlowRunResult/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterContentResultRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => ContentResultSerializer.fromObject(item));
  }

  async recentContentResult(request?: FilterContentResultRequest | null, options?: { headers?: Record<string, string> }): Promise<ContentResult[]> {
    const path = '/app/api/Object/ai.ContentFlowRunResult/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterContentResultRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => ContentResultSerializer.fromObject(item));
  }

  async dataViewContentResult(request?: FilterContentResultRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewContentResultResponse> {
    const path = '/app/api/Object/ai.ContentFlowRunResult/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterContentResultRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewContentResultResponseSerializer.fromObject(data);
  }

  async getFormForPrompt(formName: string, objectId: string, assistantId?: string | null, chatStatuses?: string[] | null, messagesRole?: string | null, messagesText?: string | null, messagesContentType?: string | null, messagesCreatedOn?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.Prompt({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (assistantId !== undefined) {
      queryParams.append('AssistantId', String(assistantId));
    }
    if (chatStatuses !== undefined) {
      queryParams.append('ChatStatuses', String(chatStatuses));
    }
    if (messagesRole !== undefined) {
      queryParams.append('Messages|Role', String(messagesRole));
    }
    if (messagesText !== undefined) {
      queryParams.append('Messages|Text', String(messagesText));
    }
    if (messagesContentType !== undefined) {
      queryParams.append('Messages|ContentType', String(messagesContentType));
    }
    if (messagesCreatedOn !== undefined) {
      queryParams.append('Messages|CreatedOn', String(messagesCreatedOn));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypePrompt(formName: string, assistantId?: string | null, chatStatuses?: string[] | null, messagesRole?: string | null, messagesText?: string | null, messagesContentType?: string | null, messagesCreatedOn?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/ai.Prompt/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (assistantId !== undefined) {
      queryParams.append('AssistantId', String(assistantId));
    }
    if (chatStatuses !== undefined) {
      queryParams.append('ChatStatuses', String(chatStatuses));
    }
    if (messagesRole !== undefined) {
      queryParams.append('Messages|Role', String(messagesRole));
    }
    if (messagesText !== undefined) {
      queryParams.append('Messages|Text', String(messagesText));
    }
    if (messagesContentType !== undefined) {
      queryParams.append('Messages|ContentType', String(messagesContentType));
    }
    if (messagesCreatedOn !== undefined) {
      queryParams.append('Messages|CreatedOn', String(messagesCreatedOn));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterPrompt(request?: FilterPromptRequest | null, options?: { headers?: Record<string, string> }): Promise<Prompt[]> {
    const path = '/app/api/Object/ai.Prompt/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterPromptRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => PromptSerializer.fromObject(item));
  }

  async createPrompt(request?: CreatePromptRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    const path = '/app/api/Object/ai.Prompt';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(CreatePromptRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async updatePrompt(objectId: string, request?: UpdatePromptRequest | null, options?: { headers?: Record<string, string> }): Promise<DataFormActionResponse> {
    let path = '/app/api/Object/ai.Prompt({objectId})'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'PATCH',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(UpdatePromptRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataFormActionResponseSerializer.fromObject(data);
  }

  async deletePrompt(objectId: string, fields?: PromptFields[] | null, ifUnmodifiedSince?: string | null, options?: { headers?: Record<string, string> }): Promise<Prompt> {
    let path = '/app/api/Object/ai.Prompt({objectId})'
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (fields !== undefined) {
      queryParams.append('fields', String(fields));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'DELETE',
      headers: {
        ...this.config.headers,
        ...(ifUnmodifiedSince !== undefined && { 'If-Unmodified-Since': String(ifUnmodifiedSince) }),
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return PromptSerializer.fromObject(data);
  }

  async recentPrompt(request?: FilterPromptRequest | null, options?: { headers?: Record<string, string> }): Promise<Prompt[]> {
    const path = '/app/api/Object/ai.Prompt/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterPromptRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => PromptSerializer.fromObject(item));
  }

  async dataViewPrompt(request?: FilterPromptRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewPromptResponse> {
    const path = '/app/api/Object/ai.Prompt/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterPromptRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewPromptResponseSerializer.fromObject(data);
  }

}
